import React, { useState } from 'react';
import { Key, Lock, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const MinhaContaTab = () => {
  const [passwordData, setPasswordData] = useState({
    password_atual: '',
    nova_password: '',
    confirmar_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    atual: false,
    nova: false,
    confirmar: false
  });
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Validação de password em tempo real
  const validacaoPassword = {
    tamanho: passwordData.nova_password.length >= 8,
    maiuscula: /[A-Z]/.test(passwordData.nova_password),
    minuscula: /[a-z]/.test(passwordData.nova_password),
    numero: /\d/.test(passwordData.nova_password),
    simbolo: /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.nova_password),
    confirmacao: passwordData.nova_password && passwordData.nova_password === passwordData.confirmar_password
  };

  const passwordValida = Object.values(validacaoPassword).every(v => v === true);

  const handleChange = (campo, valor) => {
    setPasswordData(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const toggleShowPassword = (campo) => {
    setShowPasswords(prev => ({
      ...prev,
      [campo]: !prev[campo]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!passwordValida) {
      toast.error('Por favor, cumpra todos os requisitos de password');
      return;
    }

    try {
      setSalvando(true);
      const token = localStorage.getItem('token');

      await axios.post(
        `${API_URL}/api/menu-colaborador/alterar-password`,
        passwordData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Password alterada com sucesso!');
      
      // Limpar formulário
      setPasswordData({
        password_atual: '',
        nova_password: '',
        confirmar_password: ''
      });
    } catch (error) {
      console.error('Erro ao alterar password:', error);
      toast.error(error.response?.data?.detail || 'Erro ao alterar password');
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Alterar Password</h2>
        <p className="text-sm text-gray-600 mt-1">
          Gerir a segurança da sua conta
        </p>
      </div>

      {/* Card de Alteração de Password */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Key className="h-5 w-5 mr-2" />
            Alterar Password
          </CardTitle>
          <CardDescription>
            Por segurança, a sua password deve ter pelo menos 8 caracteres e incluir letras maiúsculas, minúsculas, números e símbolos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Password Atual */}
            <div className="space-y-2">
              <Label htmlFor="password_atual">Password Atual *</Label>
              <div className="relative">
                <Input
                  id="password_atual"
                  type={showPasswords.atual ? 'text' : 'password'}
                  value={passwordData.password_atual}
                  onChange={(e) => handleChange('password_atual', e.target.value)}
                  placeholder="Digite a password atual"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowPassword('atual')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPasswords.atual ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Nova Password */}
            <div className="space-y-2">
              <Label htmlFor="nova_password">Nova Password *</Label>
              <div className="relative">
                <Input
                  id="nova_password"
                  type={showPasswords.nova ? 'text' : 'password'}
                  value={passwordData.nova_password}
                  onChange={(e) => handleChange('nova_password', e.target.value)}
                  placeholder="Digite a nova password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowPassword('nova')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPasswords.nova ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Confirmar Nova Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmar_password">Confirmar Nova Password *</Label>
              <div className="relative">
                <Input
                  id="confirmar_password"
                  type={showPasswords.confirmar ? 'text' : 'password'}
                  value={passwordData.confirmar_password}
                  onChange={(e) => handleChange('confirmar_password', e.target.value)}
                  placeholder="Confirme a nova password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowPassword('confirmar')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPasswords.confirmar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Requisitos de Password */}
            {passwordData.nova_password && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-gray-700 mb-2">Requisitos da Password:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <RequisitoItem 
                    valido={validacaoPassword.tamanho} 
                    texto="Mínimo 8 caracteres" 
                  />
                  <RequisitoItem 
                    valido={validacaoPassword.maiuscula} 
                    texto="1 letra maiúscula" 
                  />
                  <RequisitoItem 
                    valido={validacaoPassword.minuscula} 
                    texto="1 letra minúscula" 
                  />
                  <RequisitoItem 
                    valido={validacaoPassword.numero} 
                    texto="1 número" 
                  />
                  <RequisitoItem 
                    valido={validacaoPassword.simbolo} 
                    texto="1 símbolo (!@#$%...)" 
                  />
                  {passwordData.confirmar_password && (
                    <RequisitoItem 
                      valido={validacaoPassword.confirmacao} 
                      texto="Passwords coincidem" 
                    />
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPasswordData({
                  password_atual: '',
                  nova_password: '',
                  confirmar_password: ''
                })}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!passwordValida || salvando}
              >
                <Lock className="h-4 w-4 mr-2" />
                {salvando ? 'A alterar...' : 'Alterar Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const RequisitoItem = ({ valido, texto }) => (
  <div className="flex items-center gap-2 text-sm">
    {valido ? (
      <CheckCircle2 className="h-4 w-4 text-green-600" />
    ) : (
      <XCircle className="h-4 w-4 text-gray-400" />
    )}
    <span className={valido ? 'text-green-700' : 'text-gray-600'}>
      {texto}
    </span>
  </div>
);

export default MinhaContaTab;
